import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // Import autoTable
import { HOTEL_NAME, HOTEL_ADDRESS, HOTEL_PHONE, HOTEL_EMAIL, HOTEL_MOTTO } from "../Variables.js";
import HotelLogo from '../images/hotel_logo.png';

const formatDate = (dateObj) => {
  if (!dateObj) return 'N/A';
  let date;
  if (dateObj.toDate) {
    date = dateObj.toDate();
  } else {
    date = new Date(dateObj);
  }
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatShortDate = (dateObj) => {
  if (!dateObj) return 'N/A';
  let date;
  if (dateObj.toDate) {
    date = dateObj.toDate();
  } else {
    date = new Date(dateObj);
  }
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatCurrency = (amount) => {
  return amount ? parseFloat(amount).toLocaleString() : '0';
};

const getSafeText = (value, fallback = 'N/A') => {
  return value ? String(value).trim() : fallback;
};

const generateGuestBillPDF = async (booking) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  doc.setTextColor(24, 24, 24);

  // Helper: Check if we need a new page before adding content
  const checkPageBreak = (requiredHeight = 0, bottomMargin = 30) => {
    if (yPos + requiredHeight > pageHeight - bottomMargin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // --- HEADER (ONLY ON FIRST PAGE) ---
  try {
    doc.addImage(HotelLogo, 'PNG', margin, yPos, 35, 35);
  } catch (e) {}

  try {
    doc.setGState(new doc.GState({ opacity: 0.3 }));
    doc.addImage(HotelLogo, 'PNG', margin + 55, yPos + 50, 180, 180);
    doc.setGState(new doc.GState({ opacity: 1 }));
  } catch (e) {}

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(HOTEL_NAME, margin + 40, yPos + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(HOTEL_ADDRESS, margin + 40, yPos + 18);
  doc.text(`${HOTEL_PHONE} | ${HOTEL_EMAIL}`, margin + 40, yPos + 24);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'italic');
  doc.text(HOTEL_MOTTO, margin + 40, yPos + 30);

  yPos += 40;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(255, 178, 103);
  doc.text('GUEST FINAL BILL', pageWidth / 2, yPos, { align: 'center' });

  yPos += 5;
  doc.setDrawColor(255, 178, 103);
  doc.setLineWidth(1);
  doc.line(pageWidth / 2 - 60, yPos, pageWidth / 2 + 60, yPos);

  yPos += 10;

  // Guest & Stay Info
  const leftX = margin;
  const rightX = pageWidth / 2 + 10;
  let colY = yPos;

  doc.setTextColor(24, 24, 24);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('GUEST INFORMATION', leftX, colY);
  colY += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  [
    `Name: ${getSafeText(booking.guestName)}`,
    `Phone: ${getSafeText(booking.guestPhone)}`,
    `Email: ${getSafeText(booking.guestEmail)}`,
  ].forEach(line => {
    doc.text(line, leftX, colY);
    colY += 8;
  });

  colY = yPos;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('STAY DETAILS', rightX, colY);
  colY += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  [
    `Room: ${getSafeText(booking.roomNumber)} (${getSafeText(booking.roomTypeName)})`,
    `Checked-in: ${formatDate(booking.checkInActual || booking.checkIn)}`,
    `Checked-out: ${formatDate(booking.checkOutActual || booking.checkOut)}`,
  ].forEach(line => {
    doc.text(line, rightX, colY);
    colY += 8;
  });

  yPos = Math.max(colY, yPos + 10);

  // Accommodation Charges Table
  checkPageBreak(30); // Check for page break
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 178, 103);
  doc.text('ACCOMMODATION CHARGES', margin, yPos);
  yPos += 5;

  // Draw simple accommodation table
  const accTableData = [
    { label: 'Nightly Rate', value: `N${formatCurrency(booking.nightlyRate)}`, bold: false },
    { label: 'Total Nights', value: `${booking.nights || '1'}`, bold: false },
    { label: 'Total Amount', value: `N${formatCurrency(booking.totalPrice)}`, bold: true },
    { label: 'Deposit Paid', value: `N${formatCurrency(booking.deposit)}`, bold: false },
    { label: 'Balance Due', value: `N${formatCurrency(booking.balance)}`, bold: true }
  ];

  // Draw accommodation table manually
  accTableData.forEach((row, index) => {
    checkPageBreak(10);
    const bgColor = index % 2 === 0 ? [250, 250, 250] : [255, 255, 255];
    doc.setFillColor(...bgColor);
    doc.rect(margin, yPos, 100 + 60, 10, 'F');
    
    doc.setTextColor(24, 24, 24);
    doc.setFont('helvetica', row.bold ? 'bold' : 'normal');
    doc.setFontSize(12);
    doc.text(row.label, margin + 5, yPos + 7);
    doc.text(row.value, margin + 100 + 5, yPos + 7);
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, 100 + 60, 10);
    
    yPos += 10;
  });

  yPos += 10;

  // Additional Charges Table with autoTable for continuation
  let additionalTotal = 0;

  if (booking.linkedSales && booking.linkedSales.length > 0) {
    checkPageBreak(20); // Check for page break before section header
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 178, 103);
    doc.text('ADDITIONAL CHARGES', margin, yPos);
    yPos += 5;

    // Prepare data for autoTable
    const tableData = [];
    
    // Collect all products from linked sales
    booking.linkedSales.forEach(sale => {
      if (sale.products && sale.products.length > 0) {
        sale.products.forEach(product => {
          tableData.push([
            formatDate(sale.timestamp || new Date()),
            getSafeText(product.name),
            getSafeText(product.quantity, '1'),
            `N${formatCurrency(product.total || product.subtotal || 0)}`
          ]);
          additionalTotal += product.total || product.subtotal || 0;
        });
      }
    });

    // Add total row
    tableData.push([
      'TOTAL ADDITIONAL',
      '',
      '',
      `N${formatCurrency(additionalTotal)}`
    ]);

// Use autoTable for automatic page breaking
    autoTable(doc, {
    startY: yPos,
    head: [['Date/Time', 'Product Name', 'Qty', 'Amount (N)']],
    body: tableData,
    theme: 'striped',
    headStyles: { 
        fillColor: [255, 178, 103], 
        textColor: [255, 255, 255],
        fontSize: 12,
        lineColor: [255, 178, 103], // Header bottom border color
        lineWidth: 0.5
    },
    bodyStyles: { 
        textColor: [24, 24, 24],
        fontSize: 10,
        lineColor: [200, 200, 200], // Body cell border color
        lineWidth: 0.1
    },
    alternateRowStyles: { 
        fillColor: [250, 250, 250] 
    },
    margin: { left: margin, right: margin },
    styles: { 
        fontSize: 12, 
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        lineColor: [200, 200, 200], // Default line color
        lineWidth: 0.1
    },
    columnStyles: {
        0: { cellWidth: 45 }, // Date/Time
        1: { cellWidth: 65 }, // Product Name
        2: { cellWidth: 20, halign: 'center' }, // Qty
        3: { cellWidth: 30, halign: 'center' } // Amount
    },
    // Remove vertical lines
    tableLineColor: [255, 255, 255], // Make vertical lines white (invisible)
    tableLineWidth: 0,
    
    // Customize horizontal lines only
    didParseCell: function(data) {
        // Remove left and right borders for all cells
        data.cell.styles.lineWidth = {
        top: 0.1,
        right: 0,
        bottom: 0.1,
        left: 0
        };
        
        // For header, make bottom border more prominent
        if (data.section === 'head') {
        data.cell.styles.lineWidth = {
            top: 0,
            right: 0,
            bottom: 0.5,
            left: 0
        };
        data.cell.styles.lineColor = {
            top: [255, 255, 255],
            right: [255, 255, 255],
            bottom: [255, 178, 103],
            left: [255, 255, 255]
        };
        }
        
        // For total row (last row), make top border more prominent
        if (data.row.index === tableData.length - 1 && data.section === 'body') {
        data.cell.styles.lineWidth = {
            top: 0.5,
            right: 0,
            bottom: 0.1,
            left: 0
        };
        data.cell.styles.lineColor = {
            top: [255, 178, 103],
            right: [255, 255, 255],
            bottom: [200, 200, 200],
            left: [255, 255, 255]
        };
        }
    },
    didDrawPage: (data) => {
        // Update yPos after table is drawn
        yPos = data.cursor.y + 10;
    },
    willDrawCell: (data) => {
        // Style total row
        if (data.row.index === tableData.length - 1 && data.section === 'body') {
        data.cell.styles.fillColor = [40, 40, 40]; // Dark background for total
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
        }
        
        // Style header
        if (data.section === 'head') {
        doc.setFont('helvetica', 'bold');
        }
    }
    });
  }

  // Final Summary Table
  checkPageBreak(30); // Check for page break before section header
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 178, 103);
  doc.text('SUMMARY', margin, yPos);
  yPos += 5;

  const grandTotal = (booking.totalPrice || 0) + additionalTotal;
  const balance = Math.max(0, grandTotal - (booking.deposit || 0));

  const summaryData = [
    { label: 'Grand Total', value: `N${formatCurrency(grandTotal)}`, bold: false },
    { label: 'Deposit Paid', value: `N${formatCurrency(booking.deposit || 0)}`, bold: false },
    { label: 'Balance Payable', value: `N${formatCurrency(balance)}`, bold: true }
  ];

  // Draw summary table manually
  summaryData.forEach((row, index) => {
    checkPageBreak(10);
    const bgColor = index % 2 === 0 ? [250, 250, 250] : [255, 255, 255];
    if (row.bold) {
      doc.setFillColor(40, 40, 40);
    } else {
      doc.setFillColor(...bgColor);
    }
    doc.rect(margin, yPos, 100 + 60, 10, 'F');
    
    doc.setTextColor(row.bold ? 255 : 24);
    doc.setFont('helvetica', row.bold ? 'bold' : 'normal');
    doc.setFontSize(12);
    doc.text(row.label, margin + 5, yPos + 7);
    doc.text(row.value, margin + 100 + 5, yPos + 7);
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, 100 + 60, 10);
    
    yPos += 10;
  });

  yPos += 15;

  // Gratitude message
  checkPageBreak(40);
  
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(11);
  doc.setTextColor(90, 90, 90);
  doc.text("Thank you for staying with us!", margin, yPos);
  yPos += 8;
  doc.text("We hope to welcome you again soon.", margin, yPos);
  yPos += 15;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(24, 24, 24);
  doc.text('Guest Signature: ____________________________', margin, pageHeight - 25);
  doc.text('Date: __________________', pageWidth / 2 + 20, pageHeight - 25);

  const generationDateTime = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');

  // Copyright footer 

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated on ${generationDateTime} by ${getSafeText(booking.checkedOutBy?.name, 'Staff')}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
  yPos += 20;

  if (yPos < pageHeight - margin - 10) {

    yPos += 10;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`© ${new Date().getFullYear()} ${HOTEL_NAME}. All rights reserved.`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  // Add page numbers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
  }

  return doc;
};

const GuestBillPDF = {
  download: async (booking) => {
    if (!booking) return;
    try {
      const doc = await generateGuestBillPDF(booking);
      const safeName = (booking.guestName || 'guest').replace(/\s+/g, '_');
      doc.save(`Bill_${safeName}_Room${booking.roomNumber || 'XX'}.pdf`);
    } catch (err) {
      console.error("Bill PDF generation failed", err);
      alert("Could not generate bill PDF");
    }
  }
};

export default GuestBillPDF;